import React, { Fragment, useCallback, useEffect, useState } from 'react';
import SearchBar from './search-bar/search-bar';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import { useDispatch } from 'react-redux';
import { sxList } from './list.styled';
import { useMediaQuery } from '@mui/material';
import i18n from '../../../translations/strings';
import LinearLoading from '../../base/loading/linear-loading/linear-loading';
import { mediaQueryMobileLimit } from '../../../utils/constants';
import MobileRow from './mobile-row/mobileRow';
import { SvgImage, SvgType } from '../../base/svg/svg';
import moment from 'moment';

export interface ExpandedRowsProps {
  fields: any;
  selectedKeyRow: any;
}
export interface PaginationInfoInterface {
  count: number;
  pageSize: number;
  pageCount: number;
  next: string | null;
  previous: string | null;
}
export interface ListProps {
  containerComponent: any;
  data: any;
  paginationInfo?: PaginationInfoInterface;
  fields: any;
  getDataAction: any;
  expandedRows?: ExpandedRowsProps;
  searchBarVisible?: boolean;
  loading: boolean;
  statuses: any;
  expandedRowsColSpan?: number;
  refreshList?: boolean;
  showMoreButton?: boolean;
  extraParamsAPi?: any;
  showActiveEvents?: boolean;
}

const List = (props: ListProps) => {
  const {
    paginationInfo,
    expandedRowsColSpan = 7,
    showMoreButton = true,
    showActiveEvents = false,
  } = props;
  const dispatch = useDispatch();
  const currentDate = moment(new Date(), 'YYYY-MM-DD HH:mm');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchParam, setSearchParam] = useState('');
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const desktopMode = useMediaQuery(`(min-width:${mediaQueryMobileLimit}px)`);

  const handleChangePage = useCallback((event: any, newPage: number) => {
    setPage(newPage + 1);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleChangeSearch = useCallback((event: any) => {
    setSearchParam(event.target.value);
  }, []);

  const toggleRow = (selectedRow: any) => {
    const isExpandedRow = expandedRows.find((row: any) => row === selectedRow);
    if (isExpandedRow) {
      const filteredRowsSelected = expandedRows.filter(
        (row: any) => row !== isExpandedRow,
      );
      setExpandedRows(filteredRowsSelected);
    } else {
      setExpandedRows([...expandedRows, selectedRow]);
    }
  };

  useEffect(() => {
    const payload: any = {
      page: page,
      pageSize: rowsPerPage,
      search: searchParam,
      statuses: props.statuses,
    };

    if (props.extraParamsAPi) {
      const newPayloadProperty = Object.keys(props.extraParamsAPi)[0];
      payload[`${newPayloadProperty}`] =
        props.extraParamsAPi[`${newPayloadProperty}`];
    }

    if (props.refreshList) {
      dispatch(props.getDataAction(payload));
      return;
    }

    dispatch(props.getDataAction(payload));
  }, [dispatch, page, rowsPerPage, searchParam, props.refreshList]);

  return (
    <>
      {props.searchBarVisible && (
        <SearchBar
          placeholder={i18n.t('booking.searchPlaceholderReservation')}
          label={i18n.t('booking.searchLabelReservation')}
          handleChange={handleChangeSearch}
        />
      )}
      <LinearLoading loading={props.loading} />
      <TableContainer component={props.containerComponent}>
        <Table sx={{ width: '100%' }} aria-label="simple table">
          <TableHead sx={sxList.tableHeader}>
            {desktopMode && (
              <TableRow>
                {props.fields?.map((item: any, index: any) => (
                  <TableCell
                    key={index}
                    align="left"
                    sx={{
                      ...sxList.tableHeaderText,
                      width: item.customContentDisplayName && '50%',
                    }}
                  >
                    {item.customContentDisplayName
                      ? item.displayName()
                      : item.displayName.toUpperCase()}
                  </TableCell>
                ))}
                {showMoreButton && (
                  <TableCell
                    key={props.fields.length}
                    align="left"
                    sx={sxList.tableHeaderText}
                  >
                    {i18n.t('list.showMore').toUpperCase()}
                  </TableCell>
                )}
                {showActiveEvents && (
                  <TableCell
                    key={props.fields.length + 1}
                    align="left"
                    sx={sxList.tableHeaderText}
                  >
                    {i18n.t('list.active').toUpperCase()}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableHead>
          <TableBody sx={sxList.tableBody}>
            {props.data?.map((row: any) => (
              <Fragment key={row.id}>
                {desktopMode ? (
                  <TableRow>
                    {props.fields.map((item: any, index: number) =>
                      item.name === 'edit' ? (
                        <TableCell
                          sx={sxList.tableBodyText}
                          key={index}
                          align="left"
                        >
                          {item.component
                            ? item.component(row)
                            : row[item.name]}
                        </TableCell>
                      ) : (
                        <TableCell
                          onClick={(event: any) =>
                            props.expandedRows ? toggleRow(row.id) : null
                          }
                          sx={sxList.tableBodyText}
                          key={index}
                          align="left"
                        >
                          {item.component
                            ? item.component(row)
                            : row[item.name]}
                        </TableCell>
                      ),
                    )}
                    {showMoreButton && (
                      <TableCell
                        key={row.id}
                        onClick={(event: any) =>
                          props.expandedRows ? toggleRow(row.id) : null
                        }
                      >
                        {!expandedRows.includes(row.id) ? (
                          <SvgImage type={SvgType.Plus} />
                        ) : (
                          <SvgImage type={SvgType.Minus} />
                        )}
                      </TableCell>
                    )}
                    {showActiveEvents && (
                      <TableCell
                        key={'show-active-events'}
                        onClick={(event: any) =>
                          props.expandedRows ? toggleRow(row.id) : null
                        }
                      >
                        {row.interval.some(
                          (item: any) =>
                            (item.check_in && !item.check_out &&
                              currentDate.isBetween(
                                moment(item.start, 'YYYY-MM-DD HH:mm'),
                                moment(item.end, 'YYYY-MM-DD HH:mm'),
                              ))
                        ) ? (
                          <SvgImage type={SvgType.ActiveEvent} />
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ) : (
                  <MobileRow
                    row={row}
                    data={props.data}
                    fields={props.fields}
                  />
                )}
                {expandedRows.length > 0 && expandedRows.includes(row.id) && (
                  <TableRow sx={sxList.containerExpandedRow}>
                    <TableCell
                      sx={sxList.containerExpandedRowCell}
                      colSpan={expandedRowsColSpan}
                    >
                      <Table sx={{ border: 0 }}>
                        <TableHead>
                          <TableRow>
                            {props.expandedRows?.fields.map(
                              (info: any, index: any) => {
                                return (
                                  <TableCell
                                    key={index}
                                    sx={sxList.expandedRowTitle}
                                    align="left"
                                  >
                                    {(row.status === 'CONFIRMED' &&
                                      info.name === 'list_attendees') ||
                                    info.name !== 'list_attendees'
                                      ? info.displayName.toUpperCase()
                                      : null}
                                  </TableCell>
                                );
                              },
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row[props.expandedRows?.selectedKeyRow].map(
                            (info: any, index: any) => (
                              <TableRow key={index}>
                                {props.expandedRows?.fields.map(
                                  (field: any, index: any) => {
                                    if (
                                      (row.status === 'CONFIRMED' &&
                                        field.name === 'list_attendees') ||
                                      field.name !== 'list_attendees'
                                    ) {
                                      return (
                                        <Fragment key={index}>
                                          {field.component(info)}
                                        </Fragment>
                                      );
                                    } else {
                                      return null;
                                    }
                                  },
                                )}
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {paginationInfo && (
          <TablePagination
            sx={sxList.tablePagination}
            rowsPerPageOptions={desktopMode ? [15, 30] : []}
            component="div"
            count={-1}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            nextIconButtonProps={{ disabled: props.loading || !paginationInfo.next }}
            backIconButtonProps={{ disabled: props.loading || !paginationInfo.previous }}
            labelDisplayedRows={({ from, to }) =>
              i18n.t('reservations.labelDisplayedRows', {
                from: from,
                to: to,
                count: paginationInfo?.count ? paginationInfo?.count : 0,
              })
            }
            labelRowsPerPage={
              desktopMode ? i18n.t('reservations.resultsPage') : ''
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </TableContainer>
    </>
  );
};
export default List;
